/**
 * Cerberus auth context — declared in its own file so React Refresh treats
 * `AuthProvider.tsx` as component-only and `useAuth.ts` as a plain hook.
 */
import { createContext } from 'react'
import type {
  AccessClaims,
  CerberusUser,
  DomainProfileClaim,
  Grant,
  Membership,
} from './types'

export type AuthStatus = 'loading' | 'authed' | 'unauthed'

export interface AuthContextValue {
  status: AuthStatus
  user: CerberusUser | null
  claims: AccessClaims | null
  memberships: Membership[]
  grants: Grant[]
  isSuperAdmin: boolean
  activeOrgId: string | null
  setActiveOrgId: (orgId: string | null) => void
  hasGrant: (domain: string, orgId?: string) => boolean
  logout: () => void
  /**
   * T8 — currently-active domain claim (UX hint). Falls back to the first
   * granted domain when the JWT doesn't carry one (pre-T8 tokens).
   */
  activeDomain: string | null
  /** T8 — per-domain profile snapshot lifted from the JWT (may be empty). */
  domainProfiles: DomainProfileClaim[]
  /**
   * T8 — switch the active domain by re-running /auth/refresh with the
   * desired `active_domain`. Throws on refresh failure (caller decides UX).
   */
  switchDomain: (domain: string) => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)
